import {EditorState, StateEffect, StateField, Transaction} from '@codemirror/state'
import {ViewPlugin} from '@codemirror/view'
import type {Root as HtmlTree} from 'hast'
import type {Root as MarkdownTree} from 'mdast'

import {buildHtmlTree} from '../../language-tools/html/build-html'
import {parseMarkdown} from '../../language-tools/markdown/parse-markdown'

interface MarkdownParserState {
  markdownTree: MarkdownTree
  htmlTree: HtmlTree
}

function parseDocument(state: EditorState): MarkdownParserState {
  const markdownSource = state.doc.toString()
  const markdownTree = parseMarkdown(markdownSource)
  const htmlTree = buildHtmlTree(markdownTree, markdownSource)
  return {markdownTree, htmlTree}
}

/** Dispatched with the new tree when the parsed Markdown tree is updated. */
const markdownParsedEffect = StateEffect.define<MarkdownParserState>()

/**
 * Markdown parsing is relatively expensive and state updates are blocking to user interactions, so to keep the editor
 * responsive we watch for document changes and dispatch a parse asynchronously.
 *
 * We can't just do this inside the `markdownDecorationsState` plugin because `StateField`s don't have access to the
 * view -- they can't dispatch a new transaction.
 */
const asyncParseMarkdownPlugin = ViewPlugin.define(view => {
  let timeout: ReturnType<typeof setTimeout> | null = null

  return {
    update: event => {
      if (event.docChanged) {
        // Theoretically this shouldn't be necessary, but it avoids race conditions and possibly avoids extra parses
        // for several rapid updates in succession
        if (timeout) clearTimeout(timeout)
        // We might eventually want to use a Web Worker to multithread this
        timeout = setTimeout(() => view.dispatch({effects: markdownParsedEffect.of(parseDocument(view.state))}))
      }
    },
  }
})

/**
 * State field for accessing the parsed Markdown tree. Nodes are referenced by identity, so this tree is non-mutable
 * once stored in editor state.
 */
const parsedMarkdownField = StateField.define<MarkdownParserState>({
  create: parseDocument,
  update: (prev, tr) => tr.effects.find(e => e.is(markdownParsedEffect))?.value ?? prev,
  provide: () => [asyncParseMarkdownPlugin],
})

/**
 * Provides an asynchronous Markdown parser that updates as the document changes.
 * Call `getMarkdownTree` or `getHtmlTree` to obtain the parsed tree from the editor state.
 */
export const markdownParserExtension = parsedMarkdownField

/** Get the Markdown (mdast) tree from the editor state. Throws an error if `markdownParserExtension` is not installed. */
export function getMarkdownTree(state: EditorState) {
  return state.field(parsedMarkdownField).markdownTree
}

/** Get the HTML (hast) tree from the editor state. Throws an error if `markdownParserExtension` is not installed. */
export function getHtmlTree(state: EditorState) {
  return state.field(parsedMarkdownField).htmlTree
}

/**
 * Check if the parsed Markdown (mdast) tree changed in this transaction. Note that because the parser is async, this
 * will not happen in the same transaction where the document changed.
 */
export function markdownTreeChanged(transaction: Transaction) {
  return transaction.effects.some(eff => eff.is(markdownParsedEffect))
}

/**
 * Check if the parsed HTML (hast) tree changed in this transaction. Note that because the parser is async, this
 * will not happen in the same transaction where the document changed.
 *
 * For now, the HTML and Markdown trees always change together, but this may not be the case in the future.
 */
export const htmlTreeChanged = markdownTreeChanged
